import React, { useState, useEffect, useRef } from 'react';
import { bibleVerses } from './bibleVerses';
import './IchthysBibleWidget.css';

const IchthysBibleWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentVerse, setCurrentVerse] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const cardRef = useRef(null);
  const buttonRef = useRef(null);

  // Get random verse from all categories
  const getRandomVerse = () => {
    const categories = Object.keys(bibleVerses);
    const randomCategory = categories[Math.floor(Math.random() * categories.length)];
    const categoryVerses = bibleVerses[randomCategory];
    const randomVerse = categoryVerses[Math.floor(Math.random() * categoryVerses.length)];
    
    return {
      ...randomVerse,
      category: randomCategory.charAt(0).toUpperCase() + randomCategory.slice(1)
    };
  };

  // Display new verse
  const displayVerse = () => {
    const verse = getRandomVerse();
    setCurrentVerse(verse);
    
    // Store in localStorage for daily verse feature
    const today = new Date().toDateString();
    localStorage.setItem('hqt_daily_verse', JSON.stringify({
      verse,
      date: today
    }));
  };

  // Load daily verse on mount
  useEffect(() => {
    const stored = localStorage.getItem('hqt_daily_verse');
    if (stored) {
      const { verse, date } = JSON.parse(stored);
      const today = new Date().toDateString();
      
      if (date === today) {
        setCurrentVerse(verse);
      } else {
        displayVerse();
      }
    } else {
      displayVerse();
    }
  }, []);

  // Toggle card visibility
  const toggleCard = () => {
    if (!isOpen && !currentVerse) {
      displayVerse();
    }
    setIsOpen(!isOpen);
  };

  // Copy verse to clipboard
  const copyVerse = async () => {
    if (currentVerse) {
      const textToCopy = `"${currentVerse.text}" - ${currentVerse.reference}\n\n- Daily encouragement from HomeQuest Tech`;
      try {
        await navigator.clipboard.writeText(textToCopy);
        setToastMessage('Verse copied to clipboard! 📋');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2500);
      } catch (error) {
        // Fallback for mobile devices
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setToastMessage('Verse copied! 📋');
          setShowToast(true);
          setTimeout(() => setShowToast(false), 2500);
        } catch (err) {
          setToastMessage('Failed to copy verse');
          setShowToast(true);
          setTimeout(() => setShowToast(false), 2500);
        }
        document.body.removeChild(textArea);
      }
    }
  };

  // Share verse (mobile)
  const shareVerse = async () => {
    if (currentVerse && navigator.share) {
      try {
        await navigator.share({
          title: 'Daily Encouragement',
          text: `"${currentVerse.text}" - ${currentVerse.reference}\n\nShared from HomeQuest Tech`
        });
      } catch (error) {
        // Fall back to copy
        copyVerse();
      }
    } else {
      copyVerse();
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && 
          cardRef.current && 
          !cardRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (isOpen && event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <>
      {/* Widget Container */}
      <div className="ichthys-widget">
        {/* Verse Card */}
        <div 
          ref={cardRef}
          className={`verse-card ${isOpen ? 'show' : ''}`}
          role="dialog"
          aria-hidden={!isOpen}
        >
          {currentVerse && (
            <>
              {/* Header */}
              <div className="verse-header">
                <div className="verse-title">
                  <svg className="cross-icon" viewBox="0 0 24 24">
                    <path d="M11 2v7H4v4h7v9h4v-9h7v-4h-7V2h-4z"/>
                  </svg>
                  Daily Encouragement
                </div>
                <button
                  className="close-btn"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close verse"
                >
                  ×
                </button>
              </div>

              {/* Category Badge */}
              <div className="verse-category">
                {currentVerse.category}
              </div>

              {/* Verse Text */}
              <div className="verse-text">
                "{currentVerse.text}"
              </div>

              {/* Reference */}
              <div className="verse-reference">
                {currentVerse.reference}
              </div>

              {/* Action Buttons */}
              <div className="verse-actions">
                <button
                  className="next-verse-btn"
                  onClick={displayVerse}
                  aria-label="Get next verse"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M4 12a1 1 0 011-1h14a1 1 0 110 2H5a1 1 0 01-1-1z"/>
                    <path d="M13.293 4.293a1 1 0 011.414 0l7 7a1 1 0 010 1.414l-7 7a1 1 0 01-1.414-1.414L19.586 12l-6.293-6.293a1 1 0 010-1.414z"/>
                  </svg>
                  Next Verse
                </button>
                <button
                  className="share-btn"
                  onClick={shareVerse}
                  title={navigator.share ? "Share verse" : "Copy to clipboard"}
                  aria-label={navigator.share ? "Share verse" : "Copy to clipboard"}
                >
                  {navigator.share ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="18" cy="5" r="3"></circle>
                      <circle cx="6" cy="12" r="3"></circle>
                      <circle cx="18" cy="19" r="3"></circle>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  )}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Fish Button */}
        <button
          ref={buttonRef}
          className={`ichthys-button ${!isOpen ? 'pulse' : ''}`}
          onClick={toggleCard}
          aria-label="Open daily encouragement"
        >
          <svg className="ichthys-icon" viewBox="0 0 26 24">
            {/* Traditional Christian Fish (Ichthys) Symbol with Tail */}
            <path 
              d="M1 12c0 0 4-7 11-7s11 7 11 7s-4 7-11 7s-11-7-11-7z" 
              stroke="white" 
              strokeWidth="1.5" 
              fill="none"
            />
            {/* Tail at the right */}
            <path 
              d="M22 12l3-3M22 12l3 3" 
              stroke="white" 
              strokeWidth="1.5" 
              fill="none"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* Toast Notification */}
      <div className={`toast ${showToast ? 'show' : ''}`}>
        {toastMessage}
      </div>
    </>
  );
};

export default IchthysBibleWidget;